import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgoraRTC, { 
  IAgoraRTCClient, 
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack 
} from 'agora-rtc-sdk-ng';
import { api } from '../services/api.ts';
import { Event } from '../types/index.ts';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { AgoraVideoPlayer } from '../components/AgoraVideoPlayer.tsx';
import { 
  Play, Radio, Users, Eye, ArrowLeft, 
  Mic, MicOff, Video, VideoOff, PhoneOff 
} from 'lucide-react';
import { toast } from 'sonner';

interface Stream {
  stream_id: string;
  event_id: string;
  host_name: string;
  title: string;
  room_code: string;
  viewer_count: number;
  status: string;
}

export default function LiveStream() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [streams, setStreams] = useState<Stream[]>([]);
  const [selectedStream, setSelectedStream] = useState<Stream | null>(null);
  const [isHosting, setIsHosting] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [viewerCount, setViewerCount] = useState(0);
  
  const clientRef = useRef<IAgoraRTCClient | null>(null);

  useEffect(() => {
    if (eventId) {
      loadData();
    }
    return () => {
      leaveChannel();
    };
  }, [eventId]);

  const loadData = async () => {
    try {
      const [eventData, streamsData] = await Promise.all([
        api.getEvent(eventId!),
        api.getActiveStreams(eventId!)
      ]);
      setEvent(eventData);
      setStreams(streamsData || []);
    } catch (error) {
      console.error('Failed to load streams:', error);
    }
  };

  const initializeAgora = async (channelName: string, role: 'host' | 'audience') => {
    try {
      // Get Agora token from backend
      const uid = Math.floor(Math.random() * 100000);
      const tokenData = await api.getAgoraToken(channelName, uid, role === 'host' ? 'publisher' : 'subscriber');
      
      // Create client
      const client = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });
      clientRef.current = client;
      
      // Set client role
      await client.setClientRole(role);
      
      // Set up event handlers
      client.on('user-published', async (user, mediaType) => {
        await client.subscribe(user, mediaType);
        if (mediaType === 'video') {
          setRemoteUsers(prev => [...prev.filter(u => u.uid !== user.uid), user]);
        }
        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });
      
      client.on('user-unpublished', (user, mediaType) => {
        if (mediaType === 'video') {
          setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
        }
      });
      
      client.on('user-left', (user) => {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      });
      
      // Join channel
      await client.join(tokenData.appId, channelName, tokenData.token, tokenData.uid);
      setIsJoined(true);
      
      // If host, create and publish tracks
      if (role === 'host') {
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        setLocalAudioTrack(audioTrack);
        setLocalVideoTrack(videoTrack);
        await client.publish([audioTrack, videoTrack]);
      }
      
      return client;
    } catch (error) {
      console.error('Failed to initialize Agora:', error);
      throw error;
    }
  };

  const leaveChannel = async () => {
    if (localVideoTrack) {
      localVideoTrack.stop();
      localVideoTrack.close();
      setLocalVideoTrack(null);
    }
    if (localAudioTrack) {
      localAudioTrack.stop();
      localAudioTrack.close();
      setLocalAudioTrack(null);
    }
    if (clientRef.current) {
      await clientRef.current.leave();
      clientRef.current = null;
    }
    setRemoteUsers([]);
    setIsJoined(false);
  };

  const startStream = async () => {
    try {
      const stream = await api.createStream(eventId!, event?.title || 'Live Stream');
      await initializeAgora(stream.room_code, 'host');
      setIsHosting(true);
      setSelectedStream(stream);
      toast.success('Stream started!');
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to start stream');
    }
  };

  const stopStream = async () => {
    try {
      await leaveChannel();
      await api.endStream();
      setIsHosting(false);
      setSelectedStream(null);
      toast.success('Stream ended');
      await loadData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to stop stream');
    }
  };

  const joinStream = async (stream: Stream) => {
    try {
      setSelectedStream(stream);
      await initializeAgora(stream.room_code, 'audience');
      await api.joinStream(stream.stream_id);
      toast.success('Joined stream!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to join stream');
      setSelectedStream(null);
    }
  };

  const leaveStream = async () => {
    try {
      if (selectedStream) {
        await api.leaveStream(selectedStream.stream_id);
      }
      await leaveChannel();
      setSelectedStream(null);
    } catch (error) {
      console.error('Error leaving stream:', error);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(!isVideoEnabled);
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(!isAudioEnabled);
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  if (!event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Viewing/Hosting Stream View
  if (selectedStream && isJoined) {
    return (
      <div className="space-y-4 pb-20 md:pb-8">
        {/* Stream Player */}
        <Card className="glass border-white/10">
          <CardContent className="p-0">
            <div className="aspect-video bg-black rounded-t-lg relative overflow-hidden">
              {/* Show host video or remote video */}
              {isHosting && localVideoTrack ? (
                <AgoraVideoPlayer videoTrack={localVideoTrack} className="w-full h-full" />
              ) : remoteUsers.length > 0 && remoteUsers[0].videoTrack ? (
                <AgoraVideoPlayer videoTrack={remoteUsers[0].videoTrack} className="w-full h-full" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Radio className="h-16 w-16 text-primary mx-auto mb-4 animate-pulse" />
                    <p className="text-gray-400">Waiting for stream...</p>
                  </div>
                </div>
              )}
              
              {/* Live Badge */}
              <Badge className="absolute top-4 left-4 bg-red-500 animate-pulse">
                <Radio className="h-3 w-3 mr-1" />
                LIVE
              </Badge>
              
              {/* Viewer Count */}
              <Badge className="absolute top-4 right-4 bg-black/50">
                <Eye className="h-3 w-3 mr-1" />
                {selectedStream.viewer_count || viewerCount}
              </Badge>
            </div>
            
            {/* Controls */}
            <div className="p-4 flex items-center justify-between">
              <div>
                <h3 className="font-heading font-bold text-xl">{selectedStream.title}</h3>
                <p className="text-gray-400 text-sm">
                  {isHosting ? 'You are streaming' : `Hosted by ${selectedStream.host_name}`}
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                {isHosting && (
                  <>
                    <Button
                      onClick={toggleAudio}
                      variant={isAudioEnabled ? 'outline' : 'destructive'}
                      size="icon"
                      className="glass-hover"
                    >
                      {isAudioEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      onClick={toggleVideo}
                      variant={isVideoEnabled ? 'outline' : 'destructive'}
                      size="icon"
                      className="glass-hover"
                    >
                      {isVideoEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                    </Button>
                    <Button onClick={stopStream} variant="destructive">
                      <PhoneOff className="h-4 w-4 mr-2" />
                      End Stream
                    </Button>
                  </>
                )}
                {!isHosting && (
                  <Button onClick={leaveStream} variant="outline" className="glass-hover">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Leave Stream
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Other Participants Grid */}
        {remoteUsers.length > 1 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {remoteUsers.slice(1).map((user) => (
              <Card key={user.uid} className="glass border-white/10 aspect-video overflow-hidden">
                {user.videoTrack ? (
                  <AgoraVideoPlayer videoTrack={user.videoTrack} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-background-subtle">
                    <Users className="h-8 w-8 text-gray-600" />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Stream List View
  return (
    <div className="space-y-8 pb-20 md:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/discover')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold">{event.title}</h1>
            <p className="text-gray-400">Live Streams</p>
          </div>
        </div>
        <Button onClick={startStream} className="shadow-glow">
          <Radio className="h-4 w-4 mr-2" />
          Start Stream
        </Button>
      </div>

      {/* Active Streams */}
      <div className="space-y-4">
        <h2 className="text-2xl font-heading font-bold">Active Streams</h2>
        {streams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {streams.map((stream) => (
              <Card 
                key={stream.stream_id} 
                className="glass border-white/10 overflow-hidden hover:border-white/20 transition-all cursor-pointer group"
              >
                <div className="aspect-video bg-background-subtle flex items-center justify-center relative">
                  <Play className="h-16 w-16 text-primary group-hover:scale-110 transition-transform" />
                  <Badge className="absolute top-3 left-3 bg-red-500 animate-pulse">
                    <Radio className="h-3 w-3 mr-1" />
                    LIVE
                  </Badge>
                  <Badge className="absolute top-3 right-3">
                    <Eye className="h-3 w-3 mr-1" />
                    {stream.viewer_count}
                  </Badge>
                </div>
                <CardContent className="p-4 space-y-3">
                  <h3 className="font-heading font-bold text-lg line-clamp-1">{stream.title}</h3>
                  <p className="text-sm text-gray-400">Hosted by {stream.host_name}</p>
                  <Button onClick={() => joinStream(stream)} className="w-full shadow-glow">
                    <Play className="h-4 w-4 mr-2" />
                    Watch Stream
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass border-white/10 p-12 text-center">
            <Radio className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-4">No active streams</p>
            <p className="text-gray-500 mb-6">Be the first to start a live stream for this event!</p>
            <Button onClick={startStream} className="shadow-glow">
              <Radio className="h-4 w-4 mr-2" />
              Start Streaming
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
}
